<template>
    <div class="payments">
        <div class="profiles_payments">
            <h2>{{ $t('Profiles') }}</h2>
            <div class="single_profile_payment"
                 v-for="profile in profiles_payments"
                 :key="profile.segment.slug"
                 v-if="!['judgment_pending', 'disapproved', 'approved'].includes(profile.status)"
            >
                <div class="profile_picture"
                     :style="`background-image: url(${profile.picture});`"></div>
                <div class="profile_container">
                    <div class="basic_info">
                        <v-icon class="notranslate">{{ profile.segment.slug | segment_icon }}</v-icon>
                        <div class="segment">{{profile.segment.name}}</div>
                        <div class="name">{{profile.name}}</div>
                    </div>
                    <div class="status"
                         :data-status="profile.status"
                    >
                        {{ $t(`status.${profile.status}`) }}
                        {{profile.segment.offers_basic_plan
                            ? (profile.is_pro
                                ? $t('in the Pro plan')
                                : $t('in the Basic plan')
                              )
                            : ''
                        }}
                        {{ profile.status === 'active' && profile.payment_info.type !== 'free' && profile.is_pro
                            ? (profile.payment_info.hasOwnProperty('annuity_info') && profile.payment_info.annuity_info.id.toLowerCase() === 'free'
                                ? $t('on free trial')
                                : $t(`payment_type_${profile.payment_info.type}`)
                            ) : ''
                        }}
                    </div>
                    <div class="payment_info"
                         v-if="profile.status === 'file_sent'"
                    >
                        <v-icon class="notranslate">account_balance_wallet</v-icon>
                        <a href="#"
                           @click.prevent="payMembership(profile)"
                           class="single_line"
                        >{{ $t('Make the payment') }}</a>
                    </div>
                    <div class="payment_info"
                         v-if="profile.status === 'payment_pending'"
                    >
                        <v-icon class="notranslate">account_balance_wallet</v-icon>
                        <a href="#"
                           @click.prevent="payMembership(profile)"
                           class="single_line"
                        >{{ $t('Make the payment') }}</a>
                        <div class="line_2">{{ $t('in case you haven\'t') }}</div>

                    </div>
                    <div class="payment_info"
                         v-else-if="profile.status === 'active' && profile.payment_info.type !== 'free' && profile.is_pro"
                    >
                        <v-icon class="notranslate">autorenew</v-icon>
                        <div class="line_1">
                            {{ $t(`payment_info_active_${profile.payment_info.type}`) }}
                            {{profile.payment_info.type === 'annually'
                              ? diffFromNowTo(profile.payment_info.annuity_info.current_period_end * 1000)
                              : diffFromNowTo(profile.payment_info.subscription_info.current_period_end * 1000)
                            }}
                            {{ $t('days') }}
                        </div>
                        <div class="line_2">
                            {{ $t('Last transaction made on') }}
                            {{profile.payment_info.type === 'annually'
                              ? moment((profile.payment_info.annuity_info.current_period_start * 1000), $t('dateFormat'))
                              : moment((profile.payment_info.subscription_info.current_period_start * 1000), $t('dateFormat'))
                            }}
                        </div>
                    </div>
                    <div class="payment_info"
                         v-else-if="profile.status === 'active' && profile.payment_info.type === 'free'"
                    >
                        <v-icon class="notranslate">fa-medal</v-icon>
                        <div class="single_line">{{ $t('VIP profile active') }}</div>
                    </div>
                    <div class="payment_info"
                         v-else-if="profile.status === 'renewal'"
                    >
                        <v-icon class="notranslate">account_balance_wallet</v-icon>
                        <div class="line_1">
                            {{ $t('Your membership will expire in', [
                            diffFromNowTo(profile.payment_info.annuity_info.renewal_period_valid_thru * 1000)
                            ]) }}
                        </div>
                        <a href="#"
                           @click.prevent="payMembership(profile)"
                           class="single_line"
                        >{{ $t('Renew your membership') }}</a>
                    </div>
                    <div class="payment_info"
                         v-if="profile.status === 'overdue'"
                    >
                        <v-icon class="notranslate">account_balance_wallet</v-icon>
                        <a href="#"
                           @click.prevent="payMembership(profile)"
                           class="single_line"
                        >{{ $t('Reactivate your subscription') }}</a>
                    </div>
                    <div class="payment_info"
                         v-if="profile.status === 'inactive'"
                    >
                        <v-icon class="notranslate">account_balance_wallet</v-icon>
                        <a href="#"
                           @click.prevent="payMembership(profile)"
                           class="single_line"
                        >{{ $t('Reactivate your profile') }}</a>
                    </div>
                    <div class="subscription_info"
                         v-if="profile.status === 'active' && profile.payment_info.type === 'subscription'"
                    >
                        <v-icon class="notranslate">account_balance_wallet</v-icon>
                        <div class="single_line">
                            <a :href="profile.payment_info['subscription_info']['manage_url']"
                               target="_blank"
                            >{{ $t('Manage subscription') }}</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="payment_history">
            <h2>{{ $t('Payment history') }}</h2>
            <div class="single_payment"
                 v-for="payment in payment_history"
                 :key="payment.id"
            >
                <div class="payment_date">
                    <div class="month">{{ moment((payment.date_updated
                        ? payment.date_updated
                        : payment.date_created
                        ) * 1000, 'MMM')
                    }}</div>
                    <div class="day">{{ moment((payment.date_updated
                        ? payment.date_updated
                        : payment.date_created
                        ) * 1000, 'DD')
                    }}</div>
                    <div class="year">{{ moment((payment.date_updated
                        ? payment.date_updated
                        : payment.date_created
                        ) * 1000, 'YYYY')
                    }}</div>
                </div>
                <div class="payment_container">
                    <div class="details">
                        <div class="amount">
                            {{payment.details.payment_method === 'paypal' ? 'U$' : 'R$'}}
                            {{(payment.details.amount/100).toFixed(2)}}
                        </div>
                        <div class="payment_method">
                            <v-icon class="notranslate">{{ payment.details.payment_method === 'credit_card'
                                ? `credit-card_${payment.details.card_brand}`
                                : payment.details.payment_method
                                | method }}</v-icon>
                            {{(payment.details.payment_method === 'boleto'
                              ? $t('Bank slip')
                              : (payment.details.payment_method === 'paypal'
                                ? 'PayPal'
                                : (payment.details.card_last_digits
                                  ? `${$t('Credit card')} x-${payment.details.card_last_digits}`
                                  : $t('Credit card')
                                )
                              )
                            )}}
                        </div>
                    </div>
                    <div class="description">{{payment_description(payment.finality)}}</div>
                    <template v-if="payment.status !== 'paid' && payment.status !== 'completed'">
                        <template v-if="payment.details.payment_method === 'boleto'">
                            <v-alert
                                v-if="validateTime(payment.details.boleto_expiration_date)"
                                :value="true"
                                type="warning"
                                class="notranslate"
                            >
                                <strong>Pagamento pendente </strong>
                                Boleto vence em {{ moment(payment.details.boleto_expiration_date * 1000, $t('dateFormat')) }},
                                <a :href="payment.details.boleto_url" target="_blank"> imprima aqui</a>
                            </v-alert>
                            <v-alert
                                v-else
                                :value="true"
                                type="error"
                                class="notranslate"
                            >
                                Boleto venceu em {{ moment(payment.details.boleto_expiration_date * 1000, $t('dateFormat')) }}
                            </v-alert>
                        </template>
                        <template v-else>
                            <v-alert
                                :value="true"
                                type="error"
                                class="notranslate"
                            >{{ $t('Payment declined') }}</v-alert>
                        </template>
                    </template>
                </div>
            </div>
        </div>
        <v-dialog v-model="payment.dialog" max-width="470">
            <v-card>
                <v-card-title class="headline">
                    <v-icon color="success"
                            class="notranslate"
                            left
                            v-if="payment.status === 'paid'"
                    >check_circle</v-icon>
                    <v-icon color="info"
                            class="notranslate"
                            left
                            v-else-if="payment.status === 'waiting_payment'"
                    >info</v-icon>
                    <v-icon color="error"
                            class="notranslate"
                            left
                            v-else
                    >warning</v-icon>
                    {{payment.title}}
                </v-card-title>
                <v-card-text>{{payment.text}}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn flat
                           v-if="payment.status === 'refused'"
                           color="accent"
                           @click="payMembership(payment.profile)"
                    >{{ $t('Try again') }}</v-btn>
                    <v-btn flat
                           v-else
                           color="accent"
                           :href="payment.url"
                           target="_blank"
                    >{{payment.status === 'paid'
                      ? $('View profile')
                      : $t('View bank slip')
                      }}</v-btn>
                    <v-btn flat @click="payment.dialog = false">
                      {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import VAlert from 'vuetify/es5/components/VAlert/VAlert'
import paymentMixin from '../utils/payment_mixin'
import VDialog from 'vuetify/es5/components/VDialog/VDialog'
import * as moment from 'moment'
import 'moment/locale/pt-br'
import segments_style_mixin from "../utils/segments_style_mixin";
export default {
  name: 'Payments',
  mixins: [
    paymentMixin,
    segments_style_mixin
  ],
  components: {
    VAlert,
    VDialog
  },
  data: () => ({
    payment: {
      dialog: false,
      title: '',
      text: '',
      status: '',
      url: '',
      profile: null
    }
  }),
  computed: {
    profiles_payments: function () {
      if (Array.isArray(this.$store.state.segments)) {
        return this.$store.state.segments.filter(segment => {
          return segment.status !== 'empty'
        }).map(segment => {
          return {
            segment: {
              slug: segment.slug,
              name: segment.name,
              offers_basic_plan: segment.offers_basic_plan
            },
            id: segment.data.id,
            name: segment.data.name,
            picture: segment.data.picture,
            status: segment.status,
            payment_info: segment.data.payment_info,
            permalink: segment.data.permalink,
            is_pro: segment.data.is_pro
          }
        })
      }
    },
    payment_history: function () {
      if (this.$store.state.user.payment_history) {
        return this.$store.state.user.payment_history.slice().reverse()
      } else {
        return []
      }
    }
  },
  methods: {
    moment: function (date, format) {
      const lang = this.$store.state.user.language
      if (lang === 'pt') {
        moment.locale('pt-BR')
      } else {
        moment.locale('en-US')
      }
      return moment(date).format(format)
    },
    diff: function (start, add, unit) {
      const lang = this.$store.state.user.language
      if (lang === 'pt') {
        moment.locale('pt-BR')
      } else {
        moment.locale('en-US')
      }
      const a = moment(start).add(add, 'days')
      return Math.abs(moment().diff(a, unit))
    },
    diffFromNowTo: function (date) {
      const now = moment()
      const finalDate = moment(date)
      return Math.max(0, Math.round(finalDate.diff(now, 'days', true)))
    },
    validateTime: function (time) {
      return moment().isBefore(time * 1000)
    },
    invoke_payment: function () {
      alert('Realizar pagamento')
    },
    payment_description: function (finality) {
      const lang = this.$store.state.user.language
      if (finality['finality'] === 'membership') {
        let type = this.$t(`membership_type_${finality['type']}`)
        let segment = this.$t(`segment.${finality['segment']}`)
        if (lang === 'pt') {
          return `Pagamento ${type} para membresia do perfil de ${segment}`
        } else {
          return `${type} payment for ${segment} profile membership`
        }
      } else if (finality['finality'] === 'awards') {
        if (lang === 'pt') {
          return `Entradas extras no ${finality['awards_title']}`
        } else {
          return `${finality['awards_title']} extra entries`
        }
      } else if (finality['finality'] === 'review') {
        return (lang === 'pt')
          ? 'Solicitação de análise crítica'
          : 'Review request'
      } else if (finality['finality'] === 'dispute') {
        return (lang === 'pt')
          ? 'Solicitação de contestação'
          : 'Dispute request'
      } else {
        return finality['finality']
      }
    },
    payMembership: function (profile) {
      this.payment.dialog = false
      this.makePayment(
        profile['payment_info']['amount'],
        {
          finality: 'membership',
          type: profile['payment_info']['type'],
          post_id: profile['id'],
          segment: profile['segment']['slug'],
          user_id: this.$store.state.user.id
        },
        profile['payment_info']['type'] === 'annually'
          ? 'boleto, credit_card'
          : 'credit_card',
        profile['payment_info']['installments']
      ).then(result => {
        console.log(result)
        this.payment.dialog = true
        this.payment.title = result.title
        this.payment.text = result.text
        this.payment.status = result.status
        if (result.status === 'paid') {
          this.payment.url = profile['permalink']
        } else {
          this.payment.url = result.url
        }
        this.payment.profile = profile
      })
    }
  },
  filters: {
    method: function (method) {
      switch (method) {
        case 'boleto': return 'fa-file-invoice-dollar'
        case 'credit-card_mastercard': return 'fab fa-cc-mastercard'
        case 'credit-card_visa': return 'fab fa-cc-visa'
        case 'credit-card_amex': return 'fab fa-cc-amex'
        case 'credit-card_jcb': return 'fab fa-cc-jcb'
        case 'credit-card_discover': return 'fab fa-cc-discover'
        case 'credit-card_diners': return 'fab fa-cc-diners-club'
        case 'paypal': return 'fab fa-cc-paypal'
        default: return 'fa-credit-card'
      }
    }
  }
}
</script>

<style lang="scss">
    .payments {
        .profiles_payments {
            float: left;
            margin: 18px;
            width: calc(45% - 36px);
            .single_profile_payment {
                margin-top: 24px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                position: relative;
                .profile_picture {
                    width: 84px;
                    height: 84px;
                    border-radius: 50%;
                    background-size: cover;
                    background-position: center;
                }
                .profile_container {
                    width: calc(100% - 108px);
                    background: rgb(245, 245, 245);
                    padding: 18px;
                    border-radius: 12px;
                    position: relative;
                    &::before {
                        content: '';
                        display: block;
                        position: absolute;
                        top: 50%;
                        margin-top: -12px;
                        left: -24px;
                        width: 0;
                        height: 0;
                        border-top: 12px solid transparent;
                        border-right: 12px solid rgb(245, 245, 245);
                        border-bottom: 12px solid transparent;
                        border-left: 12px solid transparent;
                    }
                    .basic_info {
                        margin-bottom: 12px;
                        position: relative;
                        border-bottom: 1px solid rgba(0,0,0,0.12);
                        padding-bottom: 12px;
                        i {
                            color: rgba(0, 0, 0, 0.38);
                            position: absolute;
                            padding: 6px;
                        }
                        .segment {
                            height: 19px;
                            line-height: 19px;
                            font-size: 18px;
                            color: rgba(0,0,0,0.87);
                            padding-left: 44px;
                        }
                        .name {
                            height: 17px;
                            line-height: 17px;
                            font-size: 16px;
                            color: rgba(0,0,0,0.54);
                            padding-left: 44px;
                        }
                    }
                    .status {
                        display: inline-block;
                        height: 24px;
                        line-height: 24px;
                        font-size: 16px;
                        color: #fff;
                        margin-bottom: 12px;
                        position: relative;
                        z-index: 1;
                        &::before {
                            content: '';
                            display: block;
                            position: absolute;
                            top: 0;
                            right: -6px;
                            bottom: 0;
                            left: -6px;
                            z-index: -1;
                            transform: skewX(-6deg);
                        }
                        &[data-status="file_sent"]::before,
                        &[data-status="payment_pending"]::before,
                        &[data-status="renewal"]::before {
                            background: #f9a825;
                        }
                        &[data-status="active"]::before {
                            background: #0a893b;
                        }
                        &[data-status="inactive"]::before,
                        &[data-status="overdue"]::before {
                            background: #e5242b;
                        }
                    }
                    .manage_subscription {
                        display: inline-block;
                        height: 24px;
                        line-height: 24px;
                        font-size: 16px;
                        color: #fff;
                        margin-bottom: 12px;
                        position: relative;
                        z-index: 1;
                        margin-left: 18px;
                        background: #000;
                        opacity: 0.38;
                        padding: 0 6px;
                        border-radius: 4px;
                        text-decoration: none;
                    }
                    .manage_subscription:hover {
                        opacity: 0.54;
                    }
                    .subscription_info {
                        margin-top: 8px;
                    }
                    .payment_info,
                    .subscription_info {
                        position: relative;
                        i {
                            color: rgba(0, 0, 0, 0.38);
                            position: absolute;
                            padding: 6px;
                        }
                        .line_1 {
                            height: 19px;
                            line-height: 19px;
                            font-size: 16px;
                            color: rgba(0,0,0,0.87);
                            padding-left: 44px;
                        }
                        .line_2 {
                            line-height: 17px;
                            font-size: 15px;
                            color: rgba(0,0,0,0.54);
                            padding-left: 44px;
                        }
                        .single_line {
                            height: 36px;
                            line-height: 36px;
                            font-size: 16px;
                            color: rgba(0,0,0,0.87);
                            padding-left: 44px;
                        }
                    }
                }
            }
        }
        .payment_history {
            float: left;
            margin: 18px;
            width: calc(55% - 36px);
            .single_payment {
                margin-top: 12px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                position: relative;
                &:not(:first-of-type)::before,
                &:not(:last-of-type)::after {
                    content: '';
                    display: block;
                    position: absolute;
                    width: 2px;
                    left: 17px;
                    background: rgba(0,0,0,0.12);
                }
                &::before {
                    bottom: calc(50% + 38px);
                    top: -6px;
                }
                &::after {
                    top: calc(50% + 38px);
                    bottom: -6px;
                }
                .payment_date {
                    width: 36px;
                    margin-right: 12px;
                    font-family: 'Roboto Mono', monospace;
                    text-transform: uppercase;
                    text-align: center;
                    color: rgba(0,0,0,0.87);
                    .month {
                        font-size: 20px;
                        line-height: 20px;
                    }
                    .day {
                        font-size: 30px;
                        line-height: 30px;
                    }
                    .year {
                        font-size: 15px;
                        line-height: 15px;
                    }
                }
                .payment_container {
                    width: calc(100% - 60px);
                    background: rgb(245, 245, 245);
                    padding: 24px;
                    border-radius: 12px;
                    position: relative;
                    &::before {
                        content: '';
                        display: block;
                        position: absolute;
                        top: 50%;
                        margin-top: -12px;
                        left: -24px;
                        width: 0;
                        height: 0;
                        border-top: 12px solid transparent;
                        border-right: 12px solid rgb(245, 245, 245);
                        border-bottom: 12px solid transparent;
                        border-left: 12px solid transparent;
                    }
                    .details {
                        font-size: 16px;
                        height: 24px;
                        line-height: 24px;
                        display: flex;
                        color: rgba(0, 0, 0, 0.54);
                        > *:not(:first-child) {
                            margin-left: 24px;
                        }
                        i {
                            color: rgba(0, 0, 0, 0.38);
                            float: left;
                            margin-right: 8px;
                        }
                        .amount {
                            font-family: 'Roboto Mono', monospace;
                            font-size: 17px;
                            color: rgba(0, 0, 0, 0.87);
                            font-weight: 700;
                            position: relative;
                            z-index: 1;
                        }
                        .amount::before {
                            content: '';
                            display: none;
                            position: absolute;
                            background: #ffff00;
                            top: 0;
                            right: -6px;
                            bottom: 0;
                            left: -6px;
                            z-index: -1;
                            transform: skewX(-6deg);
                        }
                    }
                    .description {
                        padding-top: 12px;
                        font-size: 18px;
                        color: rgba(0, 0, 0, 0.87);
                    }
                    .v-alert {
                        margin: 8px 0 0 0;
                        padding: 8px;
                        .v-alert__icon {
                            margin-right: 8px;
                        }
                        a {
                            color: inherit;
                        }
                    }
                }
            }
        }
    }
</style>

<i18n>
    {
        "en": {
            "status": {
                "judgment_pending": "Membership request being analyzed",
                "disapproved": "Membership request being analyzed",
                "approved": "Approved profile",
                "file_sent": "Profile ready to be published",
                "payment_pending": "Profile with payment pending",
                "active": "Active profile",
                "renewal": "Profile renewal pending",
                "overdue": "Inactive profile",
                "inactive": "Inactive profile"
            },
            "Profiles": "Profiles",
            "in the Pro plan": "in the Pro plan",
            "in the Basic plan": "in the Basic plan",
            "on free trial": "on free trial",
            "Make the payment": "Click here to make the payment",
            "in case you haven't": "in case you haven't already",
            "payment_type_annually": "through annual payment",
            "payment_type_subscription": "through recurring payment",
            "payment_info_active_annually": "Renewal in",
            "payment_info_active_subscription": "Next payment in",
            "days": "days",
            "Last transaction made on": "Last transaction made on",
            "VIP profile active": "VIP profile active for an indefinite time",
            "Your membership will expire in": "Your membership will expire in {0} days",
            "Renew your membership": "Click here to renew your membership",
            "Reactivate your subscription": "Click here to reactivate your subscription",
            "Reactivate your profile": "Click here to reactivate your profile",
            "Manage subscription": "Manage subscription",
            "Payment history": "Payment history",
            "Bank slip": "Bank slip",
            "Credit card": "Credit card",
            "Payment declined": "Payment declined by the credit card company",
            "Try again": "Try again",
            "View profile": "View profile",
            "View bank slip": "View bank slip",
            "membership_type_annually": "Annual",
            "membership_type_subscription": "Monthly"
        },
        "pt": {
            "status": {
                "judgment_pending": "Pedido de membresia em análise",
                "disapproved": "Pedido de membresia em análise",
                "approved": "Perfil aprovado",
                "file_sent": "Perfil pronto para ser publicado",
                "payment_pending": "Perfil com pagamento pendente",
                "active": "Perfil ativo",
                "renewal": "Renovação de anuidade pendente",
                "overdue": "Perfil desativado",
                "inactive": "Perfil desativado"
            },
            "Profiles": "Perfis",
            "in the Pro plan": "no plano Pro",
            "in the Basic plan": "no plano Basic",
            "on free trial": "por cortesia",
            "Make the payment": "Clique aqui para realizar o pagamento",
            "in case you haven't": "caso você ainda não o tenha efetuado",
            "payment_type_annually": "por pagamento anual",
            "payment_type_subscription": "por pagamento recorrente",
            "payment_info_active_annually": "Renovação em",
            "payment_info_active_subscription": "Próximo pagamento em",
            "days": "dias",
            "Last transaction made on": "Última transação realizada em",
            "VIP profile active": "Perfil VIP ativo por tempo indeterminado",
            "Your membership will expire in": "Sua membresia expirará em {0} dias",
            "Renew your membership": "Clique aqui para efetuar a renovação",
            "Reactivate your subscription": "Clique aqui para reativar a sua assinatura",
            "Reactivate your profile": "Clique aqui para reativar seu perfil",
            "Manage subscription": "Gerenciar assinatura",
            "Payment history": "Histórico de pagamentos",
            "Bank slip": "Boleto bancário",
            "Credit card": "Cartão de crédito",
            "Payment declined": "Pagamento recusado pela operadora do cartão de crédito",
            "Try again": "Tentar novamente",
            "View profile": "Ver perfil",
            "View bank slip": "Visualizar boleto",
            "membership_type_annually": "Anual",
            "membership_type_subscription": "Mensal"
        }
    }
</i18n>

